import { useAppDispatch, useAppSelector } from "hooks/redux";
import { filter, sortBy } from "lodash";
import { useEffect, useLayoutEffect, useState } from "react";
import { HashLink } from "react-router-hash-link";
import { selectRfi } from "store/rfiSlice";
import { selectVendorResponse } from "store/vendorResponseSlice";
import RfiElement from "types/RfiElement";
import {
  ElementType,
  SectionGroup,
  elementTypes,
  fieldTypes,
  sectionNames,
} from "../../types/RfiTypes";
import { VendorUserType } from "auth/VendorAuth/vendorUserSlice";

export const vendorNavPages = {
  UNAUTH_DETAILS: 0,
  AUTH_DETAILS: 1,
  VENDOR_INFO: 2,
  QUESTIONNAIRE: 3,
  ATTACHMENTS: 4,
  VALIDATE: 5,
  UNAUTH_ATTACHMENTS: 6,
  QA: 7,
  UNAUTH_QUESTIONNAIRE: 8,
};

interface IVendorNavProps {
  user: VendorUserType;
  setSelectedPage?: (page:number) => void;
  selectedPage: number;
  readOnly?: boolean;
}

const VendorNav = (props: IVendorNavProps) => {
  // Hooks
  const vendorResponse = useAppSelector(selectVendorResponse);
  const rfi = useAppSelector(selectRfi);
  const dispatch = useAppDispatch();

  // State
  const [selectedSection, setSelectedSection] = useState("");
  const [hasVendorResponseId, setHasVendorResponseId] = useState(false);
  const [navHeight, setNavHeight] = useState(0);

  // Effects
  useLayoutEffect(() => {
    const nav = document.getElementById("main-navbar")?.clientHeight;
    if (nav) {
      setNavHeight(nav);
    }
  }, []);

  useEffect(() => {
    if ((vendorResponse.vendorResponse?.vendorResponseId ?? 0) > 0) {
      setHasVendorResponseId(true);
    } else {
      setHasVendorResponseId(false);
    }
  }, [vendorResponse.vendorResponse]);

  // Functions
  const sortedSectionHeadings = (
    elements: RfiElement[],
    sectionName: string,
    elementType: ElementType
  ) => {
    const sections = filter(elements, (e: RfiElement) => {
      const eSections = e.sections?.find((s: SectionGroup) => {
        return s.sectionGroupName === sectionName;
      });
      return eSections?.sectionGroupName === sectionName;
    }).filter((element: any) => {
      return (
        element.elementType === elementType &&
        element.fieldType === fieldTypes.SECTIONHEADER
      );
    });

    const sortedSections = sortBy(sections, (element: RfiElement) => {
      return element.sections?.find(
        (x: SectionGroup) => x.sectionGroupName === sectionName
      )?.sequence;
    });

    return sortedSections;
  };

  const sortedDetailsSections = sortedSectionHeadings(
    rfi.describe ?? [],
    sectionNames.DESCRIBE,
    elementTypes.FIELD
  );

  const sortedVendorInfoSections = sortedSectionHeadings(
    rfi.vendorInfo ?? [],
    sectionNames.VENDOR_INFO,
    elementTypes.SURVEY
  );

  const sortedQuestionnaireSections = sortedSectionHeadings(
    rfi.questionnaire ?? [],
    sectionNames.QUESTIONNAIRE,
    elementTypes.SURVEY
  );

  const sortedValidateSections = sortedSectionHeadings(
    rfi.finalize ?? [],
    sectionNames.FINALIZE,
    elementTypes.FIELD
  );

  // Render
  if (!props.user.isAuthenticated || !hasVendorResponseId) {
    return (
      <div
        className="rfi-nav nav flex-column nav-pills sticky-top"
        id="v-pills-tab"
        role="tablist"
        aria-orientation="vertical"
        style={{ top: navHeight }}
      >
        <div className="nav-box selected">
          <div>
            <h2 className="h5">RFI Package</h2>
          </div>
          <ul>
            <li className="list-pointer">
              <HashLink
                smooth
                to="#top"
                onClick={() => {
                  props.setSelectedPage && props.setSelectedPage(vendorNavPages.UNAUTH_DETAILS);
                  setSelectedSection(`0`);
                }}
              >
                Details
              </HashLink>
            </li>
            <li>
              <ul>
                {sortedDetailsSections.map((element: any, i: number) => {
                  return (
                    <li className="list-pointer" key={i}>
                      <HashLink
                        onClick={() => {
                          props.setSelectedPage && props.setSelectedPage(vendorNavPages.UNAUTH_DETAILS);
                          setSelectedSection(`${element.elementId}`);
                        }}
                        smooth
                        to={`#section-${element.elementId}`}
                        scroll={(el) => {
                          //console.log("el", el);
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }}
                      >
                        {" "}
                        <span
                          className={
                            props.selectedPage ===
                              vendorNavPages.UNAUTH_DETAILS &&
                            selectedSection === `${element.elementId}`
                              ? "selected"
                              : ""
                          }
                        >
                          {element.fieldLabel}
                        </span>
                      </HashLink>
                    </li>
                  );
                })}
              </ul>
            </li>
            <li className="list-pointer">
              <HashLink
                to={`#`}
                onClick={() =>
                  props.setSelectedPage && props.setSelectedPage(vendorNavPages.UNAUTH_ATTACHMENTS)
                }
              >
                <span
                  className={
                    props.selectedPage === vendorNavPages.UNAUTH_ATTACHMENTS
                      ? "selected"
                      : ""
                  }
                >
                  Attachments
                </span>
              </HashLink>
            </li>
            <li className="list-pointer">
              <HashLink
                to={`#`}
                onClick={() =>
                  props.setSelectedPage && props.setSelectedPage(vendorNavPages.UNAUTH_QUESTIONNAIRE)
                }
              >
                <span
                  className={
                    props.selectedPage === vendorNavPages.UNAUTH_QUESTIONNAIRE
                      ? "selected"
                      : ""
                  }
                >
                  Questionnaire
                </span>
              </HashLink>
            </li>
            <li className="list-pointer">
              <HashLink
                to={`#`}
                onClick={() => props.setSelectedPage && props.setSelectedPage(vendorNavPages.QA)}
              >
                <span
                  className={
                    props.selectedPage === vendorNavPages.QA ? "selected" : ""
                  }
                >
                  Q&A
                </span>
              </HashLink>
            </li>
          </ul>
        </div>
      </div>
    );
  } else {
    return (
      <>
        <div
          className="rfi-nav nav flex-column nav-pills sticky-top"
          id="v-pills-tab"
          role="tablist"
          aria-orientation="vertical"
          style={{ top: navHeight }}
        >
          {(props.selectedPage === vendorNavPages.AUTH_DETAILS ||
            props.selectedPage === vendorNavPages.UNAUTH_ATTACHMENTS ||
            props.selectedPage === vendorNavPages.QA) && (
            <div className={`nav-box selected`}>
              <div className="d-flex">
                <h2 className="h5">RFI Package</h2>
              </div>

              <ul className="list-unstyled pl-4 mt-1 mb-0 d-none d-md-block">
                <li className="list-pointer">
                  <HashLink
                    onClick={() => {
                      props.setSelectedPage && props.setSelectedPage(vendorNavPages.AUTH_DETAILS);
                      setSelectedSection(`0`);
                    }}
                    smooth
                    to="#top"
                  >
                    Details
                  </HashLink>
                </li>
                <li>
                  <ul>
                    {sortedDetailsSections.map((element: any, i: number) => {
                      return (
                        <li className="list-pointer" key={i}>
                          <HashLink
                            onClick={() => {
                              props.setSelectedPage && props.setSelectedPage(
                                vendorNavPages.AUTH_DETAILS
                              );
                              setSelectedSection(`${element.elementId}`);
                            }}
                            smooth
                            to={`#section-${element.elementId}`}
                            scroll={(el) => {
                              //console.log("el", el);
                              el.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }}
                          >
                            {" "}
                            <span
                              className={
                                props.selectedPage === 1 &&
                                selectedSection === `${element.elementId}`
                                  ? "selected"
                                  : ""
                              }
                            >
                              {element.fieldLabel}
                            </span>
                          </HashLink>
                        </li>
                      );
                    })}
                  </ul>
                </li>
                <li className="list-pointer">
                  <HashLink
                    to={`#`}
                    onClick={() =>
                      props.setSelectedPage && props.setSelectedPage(vendorNavPages.UNAUTH_ATTACHMENTS)
                    }
                  >
                    <span
                      className={
                        props.selectedPage === vendorNavPages.UNAUTH_ATTACHMENTS
                          ? "selected"
                          : ""
                      }
                    >
                      Attachments
                    </span>
                  </HashLink>
                </li>
                <li className="list-pointer">
                  <HashLink
                    to={`#`}
                    onClick={() => props.setSelectedPage && props.setSelectedPage(vendorNavPages.QA)}
                  >
                    <span
                      className={
                        props.selectedPage === vendorNavPages.QA
                          ? "selected"
                          : ""
                      }
                    >
                      Q&A
                    </span>
                  </HashLink>
                </li>
              </ul>
            </div>
          )}
          {!(
            props.selectedPage === vendorNavPages.AUTH_DETAILS ||
            props.selectedPage === vendorNavPages.UNAUTH_ATTACHMENTS ||
            props.selectedPage === vendorNavPages.QA
          ) && (
            <div
              className={`${
                props.selectedPage === vendorNavPages.AUTH_DETAILS
                  ? "nav-box selected"
                  : "details-box"
              }`}
              onClick={() => {
                props.setSelectedPage && props.setSelectedPage(vendorNavPages.AUTH_DETAILS);
                setSelectedSection(`0`);
              }}
            >
              <h2 className="h5">RFI Package</h2>
            </div>
          )}

          <div
            className={`${
              props.selectedPage === vendorNavPages.VENDOR_INFO
                ? "nav-box selected"
                : "details-box"
            }`}
            onClick={() => {
              props.setSelectedPage && props.setSelectedPage(vendorNavPages.VENDOR_INFO);
              setSelectedSection(`0`);
            }}
          >
            <div className="d-flex">
              <div className="stepCounter">
                Step <span className="count">1</span>
              </div>
              <div className="stepLabel">
                <h2 className="h5">
                  {!props.readOnly
                    ? "Update Vendor Information"
                    : "Vendor information"}{" "}
                </h2>
              </div>
            </div>
            {props.selectedPage === vendorNavPages.VENDOR_INFO && (
              <ul className="list-unstyled pl-4 mt-1 mb-0 d-none d-md-block">
                <li className="list-pointer">
                  <HashLink
                    onClick={() => {
                      props.setSelectedPage && props.setSelectedPage(vendorNavPages.VENDOR_INFO);
                      setSelectedSection(`0`);
                    }}
                    smooth
                    to="#top"
                  >
                    Top
                  </HashLink>
                </li>
                {sortedVendorInfoSections.map((element: any, i: number) => {
                  return (
                    <li className="list-pointer" key={i}>
                      <HashLink
                        key={i}
                        onClick={() => {
                          props.setSelectedPage && props.setSelectedPage(vendorNavPages.VENDOR_INFO);
                          setSelectedSection(`${element.elementId}`);
                        }}
                        smooth
                        to={`#section-${element.elementId}`}
                        scroll={(el) => {
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }}
                      >
                        <span
                          className={
                            props.selectedPage === vendorNavPages.VENDOR_INFO &&
                            selectedSection === `${element.elementId}`
                              ? "selected"
                              : ""
                          }
                        >
                          {element.fieldLabel}
                        </span>
                      </HashLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            className={`${
              props.selectedPage === vendorNavPages.QUESTIONNAIRE
                ? "nav-box selected"
                : "details-box"
            }`}
            onClick={() => {
              props.setSelectedPage && props.setSelectedPage(vendorNavPages.QUESTIONNAIRE);
              setSelectedSection(`0`);
            }}
          >
            <div className="d-flex">
              <div className="stepCounter">
                Step <span className="count">2</span>
              </div>
              <div className="stepLabel">
                <h2 className="h5">
                  {!props.readOnly ? "Complete questionnaire" : "Questionnaire"}
                </h2>
              </div>
            </div>
            {props.selectedPage === vendorNavPages.QUESTIONNAIRE && (
              <ul className="list-unstyled pl-4 mt-1 mb-0 d-none d-md-block">
                <li className="list-pointer">
                  <HashLink
                    onClick={() => {
                      props.setSelectedPage && props.setSelectedPage(vendorNavPages.QUESTIONNAIRE);
                      setSelectedSection(`0`);
                    }}
                    smooth
                    to="#top"
                  >
                    Top
                  </HashLink>
                </li>
                {sortedQuestionnaireSections.map((element: any, i: number) => {
                  return (
                    <li className="list-pointer" key={i}>
                      <HashLink
                        key={i}
                        onClick={() => {
                          props.setSelectedPage && props.setSelectedPage(vendorNavPages.QUESTIONNAIRE);
                          setSelectedSection(`${element.elementId}`);
                        }}
                        smooth
                        to={`#section-${element.elementId}`}
                        scroll={(el) => {
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }}
                      >
                        <span
                          className={
                            props.selectedPage ===
                              vendorNavPages.QUESTIONNAIRE &&
                            selectedSection === `${element.elementId}`
                              ? "selected"
                              : ""
                          }
                        >
                          {element.fieldLabel}
                        </span>
                      </HashLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            className={`${
              props.selectedPage === vendorNavPages.ATTACHMENTS
                ? "nav-box selected"
                : "details-box"
            }`}
            onClick={() => {
              props.setSelectedPage && props.setSelectedPage(vendorNavPages.ATTACHMENTS);
              setSelectedSection(`0`);
            }}
          >
            <div className="d-flex">
              <div className="stepCounter">
                Step <span className="count">3</span>
              </div>
              <div className="stepLabel">
                <h2 className="h5">
                  {!props.readOnly
                    ? "Add any additional attachments needed"
                    : "Additional attachments"}
                </h2>
              </div>
            </div>
          </div>

          <div
            className={`${
              props.selectedPage === vendorNavPages.VALIDATE
                ? "nav-box selected"
                : "details-box"
            }`}
            onClick={() => {
              props.setSelectedPage && props.setSelectedPage(vendorNavPages.VALIDATE);
              setSelectedSection(`0`);
            }}
          >
            <div className="d-flex">
              <div className="stepCounter">
                Step <span className="count">4</span>
              </div>
              <div className="stepLabel">
                <h2 className="h5">
                  {!props.readOnly
                    ? "Validate and submit response"
                    : "Submission Information"}{" "}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default VendorNav;
